import React, { useState } from 'react';
import './FilterSidebar.css';
import PropTypes from 'prop-types';
import FinancialYearFilter from '../Tables/FinancialYearFilter';
import SchemeDropdown from '../Tables/SchemeDropdown';
import osm from '../../../assets/images/osm_map_img.png';
import satellite from '../../../assets/images/satellite_map_img.png';
import terrain from '../../../assets/images/terrain_map_img.png';

const FilterSidebar = ({
  userVisible,
  selectedState,
  selectedDistrict,
  selectedVillage,
  filteredDistricts,
  filteredVillages,
  stateData,
  onStateSelect,
  onDistrictSelect,
  onReset,
  schemeSelectorProps,
  selectedSchemes,
  selectedFinancialYear,
  onFinancialYearChange,
  financialYearLoading,
  schemeCounts,
  selectedScheme,
  onSchemeChange,
  schemeColors,
  onStateDataLoaded,
  // MAP SERVICE PROPS
  mapService,
  onMapServiceChange,
  // BACK BUTTON PROPS
  onBackNavigation,
  currentView,
  hasHistory,
  navigationHistory
}) => {
  // State for modal visibility
  const [showStateModal, setShowStateModal] = useState(false);
  const [showDistrictModal, setShowDistrictModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Create updated scheme selector props with filtered schemes
  const updatedSchemeSelectorProps = {
    ...schemeSelectorProps,
    selectedSchemes: selectedSchemes
  };

  // Handler for map service change
  const handleMapServiceChange = (service) => {
    if (onMapServiceChange) {
      onMapServiceChange(service);
    }
  };

  // Handler for back navigation
  const handleBackClick = () => {
    if (onBackNavigation) {
      onBackNavigation();
    }
  };

  // Handle state selection from modal
  const handleStateSelect = (stateName) => {
    if (onStateSelect && stateName) {
      console.log("📍 State selected from filter:", stateName);
      onStateSelect(stateName);
      setShowStateModal(false);
      setSearchTerm('');
    }
  };

  // Handle district selection from modal
  const handleDistrictSelect = (districtName) => {
    if (onDistrictSelect && districtName && filteredDistricts) {
      const districtFeature = filteredDistricts.find(
        district => district.properties?.DISTRICT === districtName
      );
      if (districtFeature) {
        console.log("📍 District selected from filter:", districtName);
        onDistrictSelect(districtFeature);
        setShowDistrictModal(false);
        setSearchTerm('');
      }
    }
  };

  // Filter states based on search
  const getFilteredStates = () => {
    if (!stateData?.features) return [];
    return stateData.features
      .slice()
      .sort((a, b) => a.properties.STATE.localeCompare(b.properties.STATE))
      .filter(state => 
        state.properties.STATE.toLowerCase().includes(searchTerm.toLowerCase())
      );
  };

  // Filter districts based on search
  const getFilteredDistricts = () => {
    if (!filteredDistricts) return [];
    return filteredDistricts
      .slice()
      .sort((a, b) => a.properties.DISTRICT.localeCompare(b.properties.DISTRICT))
      .filter(district => 
        district.properties.DISTRICT.toLowerCase().includes(searchTerm.toLowerCase())
      );
  };

  // Determine if back button should be shown
  const shouldShowBackButton = currentView !== 'india' || hasHistory;

  // Check if a scheme is selected to enable state filter
  const isSchemeSelected = selectedScheme && Object.keys(selectedScheme).length > 0;

  if (!userVisible) return null;

  return (
    <aside className="leftMapSidebar">
      <div className="row mt-1">
        <div className="col d-flex justify-content-center flex-wrap">
          <div className="col-md-12">
            <div className="row g-1">
              {/* Financial Year Filter */}
              <div className="col-md-6">
                <FinancialYearFilter
                  selectedFinancialYear={selectedFinancialYear}
                  onFinancialYearChange={onFinancialYearChange}
                  financialYearLoading={financialYearLoading}
                />
              </div>

              {/* Scheme Dropdown */}
              <div className="col-md-6">
                <SchemeDropdown
                  schemeCounts={schemeCounts}
                  selectedScheme={selectedScheme}
                  onSchemeChange={onSchemeChange}
                  schemeColors={schemeColors}
                  onStateDataLoaded={onStateDataLoaded}
                  selectedFinancialYear={selectedFinancialYear} 
                />
              </div>

              {/* State Selection - Modal Trigger */}
              <div className="col-md-6">
                <div 
                  className={`form-select mb-2 state-selector ${!isSchemeSelected ? 'disabled' : ''}`}
                  onClick={() => isSchemeSelected && setShowStateModal(true)}
                  style={{ 
                    cursor: isSchemeSelected ? 'pointer' : 'not-allowed',
                    backgroundColor: '#fff',
                    height: '38px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {selectedState?.properties?.STATE || 'Select a State'}
                </div>
              </div>

              {/* District Selection - Modal Trigger */}
              <div className="col-md-6">
                <div 
                  className={`form-select mb-2 district-selector ${!filteredDistricts?.length || !isSchemeSelected ? 'disabled' : ''}`}
                  onClick={() => (filteredDistricts?.length && isSchemeSelected) && setShowDistrictModal(true)}
                  style={{ 
                    cursor: (filteredDistricts?.length && isSchemeSelected) ? 'pointer' : 'not-allowed',
                    backgroundColor: '#fff',
                    height: '38px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {selectedDistrict?.properties?.DISTRICT || 'Select a District'}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="col-md-12">
            <div className="row">
              <div className="d-flex justify-content-center gap-2 mb-3">
                
                {/* Back Navigation Button */}
                {shouldShowBackButton && (
                  <button
                    className="btn btn-primary leftbhuvan"
                    onClick={handleBackClick}
                    title="Navigate back to previous view"
                  >
                    <i className="bi bi-arrow-left"></i>
                    {currentView === 'state'
                      ? 'Back'
                      : currentView === 'district'
                      ? `Back to ${selectedState?.properties?.STATE || 'State'} View`
                      : 'Back'}
                  </button>
                )}

                {/* Reset Button */}
                <button
                  className="btn btn-primary leftbhuvan"
                  onClick={onReset}
                >
                  <i className="bi bi-arrow-clockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* State Selection Modal - Reduced Size */}
      {showStateModal && (
        <div className="modal-overlay" onClick={() => setShowStateModal(false)}>
          <div 
            className="modal-content" 
            onClick={e => e.stopPropagation()}
            style={{
              width: '320px',
              maxWidth: '90vw',
              maxHeight: '70vh'
            }}
          >
            <div className="modal-header selectstate" style={{ padding: '12px 16px' }}>
              <h2 style={{ fontSize: '18px', margin: 0 }}>Select a State</h2>
              <button 
                className="close-btn" 
                onClick={() => setShowStateModal(false)}
                style={{ fontSize: '24px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            <div className="modal-search" style={{ padding: '8px 16px' }}>
              <input
                type="text"
                placeholder="Search states..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  outline: 'none'
                }}
              />
            </div>
            <div 
              className="modal-list" 
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '8px 0'
              }}
            >
              {getFilteredStates().map((state) => (
                <div
                  key={state.properties.STATE}
                  className={`modal-item ${selectedState?.properties?.STATE === state.properties.STATE ? 'selected' : ''}`}
                  onClick={() => handleStateSelect(state.properties.STATE)}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                >
                  {state.properties.STATE}
                </div>
              ))}
              {getFilteredStates().length === 0 && (
                <div className="no-results" style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                  No states found
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* District Selection Modal - Reduced Size */}
      {showDistrictModal && (
        <div className="modal-overlay" onClick={() => setShowDistrictModal(false)}>
          <div 
            className="modal-content" 
            onClick={e => e.stopPropagation()}
            style={{
              width: '320px',
              maxWidth: '90vw',
              maxHeight: '70vh'
            }}
          >
            <div className="modal-header selectstate" style={{ padding: '12px 16px' }}>
              <h2 style={{ fontSize: '18px', margin: 0 }}>
                Select a District in {selectedState?.properties?.STATE || ''}
              </h2>
              <button 
                className="close-btn" 
                onClick={() => setShowDistrictModal(false)}
                style={{ fontSize: '24px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            <div className="modal-search" style={{ padding: '8px 16px' }}>
              <input
                type="text"
                placeholder="Search districts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  outline: 'none'
                }}
              />
            </div>
            <div 
              className="modal-list" 
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '8px 0'
              }}
            >
              {getFilteredDistricts().map((district) => (
                <div
                  key={district.properties.DISTRICT}
                  className={`modal-item ${selectedDistrict?.properties?.DISTRICT === district.properties.DISTRICT ? 'selected' : ''}`}
                  onClick={() => handleDistrictSelect(district.properties.DISTRICT)}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                >
                  {district.properties.DISTRICT}
                </div>
              ))}
              {getFilteredDistricts().length === 0 && (
                <div className="no-results" style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                  No districts found
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

FilterSidebar.propTypes = {
  userVisible: PropTypes.bool,
  selectedState: PropTypes.object,
  selectedDistrict: PropTypes.object,
  selectedVillage: PropTypes.object,
  filteredDistricts: PropTypes.array,
  filteredVillages: PropTypes.array,
  stateData: PropTypes.object,
  onStateSelect: PropTypes.func,
  onDistrictSelect: PropTypes.func,
  onReset: PropTypes.func,
  schemeSelectorProps: PropTypes.object,
  selectedSchemes: PropTypes.array,
  selectedFinancialYear: PropTypes.string,
  onFinancialYearChange: PropTypes.func,
  financialYearLoading: PropTypes.bool,
  schemeCounts: PropTypes.array,
  selectedScheme: PropTypes.object,
  onSchemeChange: PropTypes.func,
  schemeColors: PropTypes.object,
  onStateDataLoaded: PropTypes.func,
  // MAP SERVICE PROPS
  mapService: PropTypes.string,
  onMapServiceChange: PropTypes.func,
  // BACK BUTTON PROPS
  onBackNavigation: PropTypes.func,
  currentView: PropTypes.string,
  hasHistory: PropTypes.bool,
  navigationHistory: PropTypes.array
};

export default FilterSidebar;